/**
 * sb-motion.js — SoundBird motion helper (CUSTOM, upgrade-safe)
 *
 * One job: when a value inside the cart drawer changes after a cart update
 * (a line's price or the subtotal), give the new value a micro "tick" — the
 * .sb-motion-pulse animation defined in snippets/sb-motion.liquid. (The cart
 * header carries no superscript item count — owner ask, the old cart-header snippet.)
 *
 * Implementation: a MutationObserver on #cart-drawer watching text changes,
 * rather than cart events — the drawer is re-rendered via morph after each
 * Section Rendering API round-trip, and observing the DOM directly means we
 * pulse exactly when (and only when) a watched value visibly changes,
 * regardless of which component triggered the update. Vanilla, no deps.
 */

const PULSE_CLASS = 'sb-motion-pulse';
const WATCHED = '.cart-items__price, .cart-totals__total-value';
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* Frosted-glass gate for the unified scrim recipe (the old sheet-morph layer):
   the scrims' blur radius is calc()'d from the registered custom property
   --sb-scrim-a, so it only ramps smoothly where @property is real
   (CSSPropertyRule: Chrome 85+, Safari 16.4+, Firefox 128+). Engines without
   it never get .sb-glass and keep a clean tint-only fade — a missing blur
   beats a blur that pops in at full strength. */
if (typeof CSSPropertyRule !== 'undefined') {
  document.documentElement.classList.add('sb-glass');
}

/** @param {Element} el */
function pulse(el) {
  if (reducedMotion.matches) return;
  // Restart cleanly if a pulse is already running on this element.
  el.classList.remove(PULSE_CLASS);
  // Force a reflow so re-adding the class restarts the animation.
  void /** @type {HTMLElement} */ (el).offsetWidth;
  el.classList.add(PULSE_CLASS);
  el.addEventListener('animationend', () => el.classList.remove(PULSE_CLASS), { once: true });
}

function init() {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer) return;

  /** Last known text per watched element (pulse only on real changes). */
  const known = new WeakMap();
  const snapshot = () => {
    for (const el of drawer.querySelectorAll(WATCHED)) known.set(el, el.textContent);
  };
  snapshot();

  const observer = new MutationObserver((mutations) => {
    /** @type {Set<Element>} */
    const changed = new Set();

    for (const mutation of mutations) {
      // Morph updates values in place → characterData mutations.
      if (mutation.type === 'characterData') {
        const el = mutation.target.parentElement?.closest(WATCHED);
        if (el && known.get(el) !== el.textContent) changed.add(el);
      }
      // Wholesale node replacement (e.g. a re-rendered summary block).
      if (mutation.type === 'childList') {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          const els = node.matches(WATCHED) ? [node] : node.querySelectorAll(WATCHED);
          for (const el of els) {
            if (known.has(el) && known.get(el) === el.textContent) continue;
            // Unseen node: only pulse if some prior sibling knowledge exists —
            // i.e. skip the very first population of the drawer.
            if (known.has(el)) changed.add(el);
          }
        }
      }
    }

    // Re-snapshot before pulsing so rapid successive updates diff correctly.
    snapshot();
    for (const el of changed) pulse(el);
  });

  observer.observe(drawer, { subtree: true, childList: true, characterData: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
